'use client';
import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';

const InfrastructureHero = () => {
    return (
        <section className="rpl-hero-section rpl-inner-hero-section bg-[#050a10]">
            {/* High-Resolution Infrastructure Background */}
            <motion.div
                initial={{ scale: 1.1, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0"
                style={{ backgroundImage: "url('/images/advanced_label_infrastructure.png')" }}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-[#050a10] via-[#050a10]/40 to-transparent"></div>
            </motion.div>

            {/* Breadcrumb Wrapper for perfect vertical alignment */}
            <div className="rpl-hero-breadcrumb-wrapper">
                <div className="rpl-hero-breadcrumb-container">
                    <nav className="rpl-hero-breadcrumb">
                        <Link href="/" className="hover:text-white transition-colors">Home</Link>
                        <ChevronRight size={14} />
                        <span className="text-white">Our Infrastructure</span>
                    </nav>
                </div>
            </div>

            <div className="rpl-hero-frame">
                <div className="flex justify-start items-center w-full">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="rpl-hero-card"
                    >
                        {/* Ultra-Premium Infrastructure Glass Card */}
                        <div className="rpl-hero-card-inner rpl-service-card-inner">
                            {/* Ambient highlight line */}
                            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

                            <div className="flex flex-col items-start w-full relative z-10 gap-fluid-lg">
                                <div className="flex flex-col items-start w-full gap-fluid-md">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2, duration: 0.6 }}
                                    >
                                        <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-white/90 type-meta">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#3672ae] mr-3 animate-pulse" />
                                            Technological Leadership
                                        </span>
                                    </motion.div>

                                    <motion.h1
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4, duration: 0.6 }}
                                        className="rpl-hero-headline text-left w-full"
                                    >
                                        <span className="block font-serif italic text-white/90 mb-2">World-Class</span>
                                        <span className="block text-white">Infrastructure.</span>
                                    </motion.h1>

                                    <motion.p
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.6, duration: 0.6 }}
                                        className="rpl-hero-body text-left text-white/60"
                                    >
                                        At Royal Prime Labels, we combine <span className="text-white font-medium">advanced European technology</span> with industrial-scale capacity to deliver labels that define brand excellence.
                                    </motion.p>
                                </div>

                                {/* Refined Stats Grid */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.8, duration: 0.6 }}
                                    className="rpl-infra-hero-stats border-y border-white/10 w-full"
                                >
                                    <div className="flex flex-col items-start gap-1">
                                        <span className="rpl-infra-hero-stats-num">15+</span>
                                        <span className="rpl-infra-hero-stats-label">Lines</span>
                                    </div>
                                    <div className="flex flex-col items-start gap-1">
                                        <span className="rpl-infra-hero-stats-num">2M+</span>
                                        <span className="rpl-infra-hero-stats-label">Capacity</span>
                                    </div>
                                    <div className="flex flex-col items-start gap-1">
                                        <span className="rpl-infra-hero-stats-num">24/7</span>
                                        <span className="rpl-infra-hero-stats-label">Ops Cycle</span>
                                    </div>
                                    <div className="flex flex-col items-start gap-1">
                                        <span className="rpl-infra-hero-stats-num">0.1<small className="text-[0.6em] font-serif">mm</small></span>
                                        <span className="rpl-infra-hero-stats-label">Precision</span>
                                    </div>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.0, duration: 0.6 }}
                                    className="rpl-hero-buttons"
                                >
                                    <Link href="/quote" className="no-tap-enforce w-full sm:w-auto">
                                        <button className="rpl-infra-btn-primary">
                                            Explore Machinery
                                        </button>
                                    </Link>
                                    <Link href="/contact" className="no-tap-enforce w-full sm:w-auto">
                                        <button className="rpl-infra-btn-outline">
                                            Partner with Us
                                        </button>
                                    </Link>
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default InfrastructureHero;
